import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Download,
  Bot,
  Globe,
  Zap,
  Terminal,
  Code2,
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

const Home = () => {
  const features = [
    {
      icon: <Bot className="h-6 w-6" />,
      title: "AI-Powered Assistant",
      description:
        "Chat interface with GPT-5 support, session categories, slash commands, and intelligent automation",
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Browser Automation",
      description:
        "Real browser control with Playwright, accessibility tree navigation, and persistent profiles",
    },
    {
      icon: <Terminal className="h-6 w-6" />,
      title: "Comprehensive Tool Ecosystem",
      description:
        "Extensive MCP and internal tools including Notes, Dependency Map, Azure DevOps, and more - all pre-configured and ready to use",
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Advanced Automation",
      description:
        "Scheduled tasks, PR reviews, bug reproduction assistant, and workflow automation",
    },
    {
      icon: <Code2 className="h-6 w-6" />,
      title: "Developer Tools",
      description:
        "Dependency mapping, terminal automation, code analysis, and semantic search",
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Smart Features",
      description:
        "Token tracking, cost monitoring, prompt enhancement, and context-aware sidebar",
    },
  ];

  const stats = [
    { value: "AI-First", label: "Approach" },
    { value: "10+", label: "Integrations" },
    { value: "Simple", label: "Setup" },
    { value: "Cross", label: "Platform" },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-background via-purple-950/10 to-background">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20">
          <div className="max-w-5xl mx-auto text-center space-y-8 py-20">
            <div className="relative mb-12 flex justify-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-yellow-500 rounded-full blur-2xl opacity-50 group-hover:opacity-75 transition-opacity duration-500 animate-pulse"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400/30 to-yellow-400/30 rounded-full blur-xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_webbie-hub/artifacts/x9adfrff_Cogsmith-logo.png"
                  alt="Cogsmith"
                  className="relative w-48 h-48 md:w-64 md:h-64 object-contain drop-shadow-2xl transform group-hover:scale-105 transition-transform duration-500"
                  style={{
                    filter: 'drop-shadow(0 0 40px rgba(6, 182, 212, 0.4))',
                  }}
                />
                <div className="absolute -inset-4 border border-purple-500/20 rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
              </div>
            </div>
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 backdrop-blur-sm">
              <Sparkles className="h-4 w-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">
                Buy Once, Keep Forever
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-purple-400 via-yellow-400 to-emerald-400 bg-clip-text text-transparent">
                AI Desktop Assistant
              </span>
              <br />
              <span className="text-foreground">for Coders & QA Analysts</span>
            </h1>

            <p className="text-xl text-foreground/70 max-w-2xl mx-auto">
              Chat-native desktop app with AI assistants, browser automation,
              bug reproduction tracking, and comprehensive tools to supercharge
              your productivity.
            </p>

            {/* Product Hunt Badge */}
            <div className="flex justify-center my-6">
              <a href="https://www.producthunt.com/products/cogsmith?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-cogsmith" target="_blank" rel="noopener noreferrer">
                <img src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1034955&theme=light&t=1762481229275" alt="Cogsmith - AI Desktop Assistant for Coders & QA Analysts | Product Hunt" style={{ width: '250px', height: '54px' }} width="250" height="54" />
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/download">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-purple-500 to-yellow-500 hover:from-purple-600 hover:to-yellow-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300 text-lg px-8 py-6"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download for Free
                </Button>
              </Link>
              <Link to="/features">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50 text-lg px-8 py-6"
                >
                  Explore Features
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-8 pt-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent"></div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Powerful Features
              </span>
            </h2>
            <p className="text-xl text-foreground/60 max-w-2xl mx-auto">
              Everything you need to automate, integrate, and accelerate your
              workflow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30"
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-foreground/60">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/features">
              <Button
                size="lg"
                variant="outline"
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50"
              >
                View All Features
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-background to-purple-950/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold">
                Built for
                <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                  {" "}
                  Developers
                </span>
              </h2>
              <p className="text-lg text-foreground/70">
                Cogsmith takes an AI-first approach to developer workflows.
                Unlike complex IDEs requiring extensive configuration, Cogsmith
                provides focused, pre-configured tools that just work out of the box.
              </p>
              <ul className="space-y-4">
                {[
                  "Zero-configuration setup - tools work immediately",
                  "AI-first workflow automation for common developer tasks",
                  "Focused feature set designed for specific workflows",
                  "Pre-integrated tools: Notes, Dependency Map, Azure DevOps, Browser Automation",
                  "No config file editing or complex setup required",
                  "Simple, intuitive interface - it just works",
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-purple-400 flex-shrink-0" />
                    <span className="text-foreground/80">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about">
                <Button
                  variant="outline"
                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50 mt-4"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-yellow-500/20 rounded-2xl blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1707528041466-83a325f01a3c?auto=format&fit=crop&w=800"
                alt="Developer Workspace"
                className="relative rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="overflow-hidden bg-gradient-to-br from-purple-950/30 to-purple-900/30 border-purple-500/20">
            <CardContent className="p-12 text-center space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold">
                <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                  Ready to Get Started?
                </span>
              </h2>
              <p className="text-xl text-foreground/70 max-w-2xl mx-auto">
                Download Cogsmith now and experience the future of AI-powered
                desktop automation. Buy once, keep forever.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Link to="/download">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-purple-500 to-yellow-500 hover:from-purple-600 hover:to-yellow-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300 text-lg px-8 py-6"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Download Now
                  </Button>
                </Link>
                <Link to="/docs">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50 text-lg px-8 py-6"
                  >
                    Read Documentation
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;